import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AiOutlineSearch } from 'react-icons/ai';
import { FaPlus } from 'react-icons/fa';
import { Button, InputGroup, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { State } from 'country-state-city';

import Wrapper from '../../components/Wrapper';
import SupportPagination from '../../components/utils/supportPagination';
import ClinicTable from '../../components/tables/ClinicTable';
import axiosInstance from '../../utils/axios';
import { getRole } from '../../utils/role';
import { hasPermission } from '../../hooks/usePermission';

const LIMIT = 15;

const Clinics = () => {
  const navigate = useNavigate();
  const role = getRole();

  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  
  // New state variables for the dropdown
  const [selectedState, setSelectedState] = useState('');
  const [submittedState, setSubmittedState] = useState('');
  
  const [clinics, setClinics] = useState([]);
  const [status, setStatus] = useState('loading');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const indianStates = State.getStatesOfCountry('IN');

  const loadData = useCallback(async (pageToFetch = currentPage, keyword = submittedSearch, stateFilter = submittedState) => {
    setStatus('loading');
    try {
      const endpoint = role === 'SUPPORT' ? '/super-admin/clinics' : '/admin/clinics';
      
      const response = await axiosInstance.get(endpoint, {
        params: {
          page: pageToFetch,
          limit: LIMIT,
          keyword: keyword || undefined,
          state: stateFilter || undefined,
        }
      });
      
      setClinics(response.data.data || []);
      
      if (response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages);
        setCurrentPage(response.data.pagination.currentPage);
      }
    } catch (error) {
      console.error('Error fetching clinics:', error);
    } finally {
      setStatus('idle');
    }
  }, [currentPage, submittedSearch, submittedState, role]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
  }, [submittedSearch, submittedState, loadData]);

  const handleSearch = () => {
    setSubmittedSearch(searchTerm.trim());
    setSubmittedState(selectedState);
    if (currentPage !== 1) {
      setCurrentPage(1);
      loadData(1, searchTerm.trim(), selectedState);
    } else {
      loadData(1, searchTerm.trim(), selectedState);
    }
  };

  // Status Toggle Handler
  const handleToggleStatus = async (clinicId, currentStatus) => {
    const newStatus = currentStatus === 'FUNCTIONAL' ? 'NON_FUNCTIONAL' : 'FUNCTIONAL';
    
    const result = await Swal.fire({
      title: 'Change Status?',
      text: `Are you sure you want to mark this clinic as ${newStatus}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, change it!'
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.put(`/super-admin/clinics/${clinicId}/status`, { status: newStatus });
        Swal.fire('Updated!', 'Clinic status has been changed.', 'success');
        loadData(currentPage, submittedSearch, submittedState);
      } catch (error) {
        console.error('Error updating status:', error);
        Swal.fire('Error', 'Failed to update status.', 'error');
      }
    }
  };

  const startIndex = (currentPage - 1) * LIMIT;

  const reduxCompatibleSetCurrentPage = (pageNum) => {
    setCurrentPage(pageNum);
    return { type: 'DUMMY', payload: pageNum };
  };

  const reduxCompatibleFetchData = (params) => {
    loadData(params.page, params.keyword, submittedState);
    return { type: 'DUMMY', payload: params };
  };

  const handleStateChange = (e) => {
    const newState = e.target.value;
    setSelectedState(newState);
    setSubmittedState(newState);
    
    setCurrentPage(1);
    loadData(1, submittedSearch, newState);
  };

  return (
    <Wrapper>
      <div className='py-4'>
        <div className='d-flex flex-column flex-md-row justify-content-between mb-3 w-100'>
          <h1 className='fw-bolder'>Soil Doctor Clinics</h1>

          <div className='d-flex flex-column flex-md-row justify-content-end align-items-center mt-3 m-md-0 gap-3'>
            <InputGroup className="flex-nowrap">
              <Form.Select
                value={selectedState}
                onChange={handleStateChange}
                className="bg-light border-secondary"
                style={{ maxWidth: '200px' }}
              >
                <option value="">All States</option>
                {indianStates.map((state) => (
                  <option key={state.isoCode} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </Form.Select>
              
              <Form.Control
                type='text'
                className='bg-light border-secondary'
                placeholder='Search by Code, Admin, or FO'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button variant='outline-secondary' onClick={handleSearch}>
                <AiOutlineSearch />
              </Button>
            </InputGroup>

            <div className="d-flex flex-wrap gap-2">
              {hasPermission("clinics:add") && (
                <button
                  className="btn btn-dark text-nowrap d-flex align-items-center gap-2"
                  onClick={() => {
                    const addRoute = '/clinics/add'
                    navigate(addRoute);
                  }}
                >
                  <FaPlus /> Add Clinic
                </button>
              )}
            </div>
          </div>
        </div>

        {status === 'loading' ? (
          <div className="d-flex justify-content-center py-5">
            <h4 className='text-muted'>Loading clinics...</h4>
          </div>
        ) : (
          <>
            <ClinicTable 
              clinics={clinics} 
              startIndex={startIndex} 
              role={role} 
              onToggleStatus={handleToggleStatus} 
            />
            
            <SupportPagination
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={reduxCompatibleSetCurrentPage}
              fetchData={reduxCompatibleFetchData}
              searchKeyword={submittedSearch}
            />
          </>
        )}
      </div>
    </Wrapper>
  );
};

export default Clinics;